import * as THREE from "three";
import { B, SOLID, type World } from "../shared/world";
import { ATLAS_COLS, ATLAS_ROWS, blockTile } from "./textures";

// dir: 0 +x,1 -x,2 +y,3 -y,4 +z,5 -z — corners listed counter-clockwise from outside.
// Shades are Minecraft's: top 1.0, north/south 0.8, east/west 0.6, bottom 0.5.
const FACES: { n: [number, number, number]; c: [number, number, number][]; shade: number }[] = [
  { n: [1, 0, 0], c: [[1, 0, 1], [1, 0, 0], [1, 1, 0], [1, 1, 1]], shade: 0.6 },
  { n: [-1, 0, 0], c: [[0, 0, 0], [0, 0, 1], [0, 1, 1], [0, 1, 0]], shade: 0.6 },
  { n: [0, 1, 0], c: [[0, 1, 1], [1, 1, 1], [1, 1, 0], [0, 1, 0]], shade: 1 },
  { n: [0, -1, 0], c: [[0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1]], shade: 0.5 },
  { n: [0, 0, 1], c: [[0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1]], shade: 0.8 },
  { n: [0, 0, -1], c: [[1, 0, 0], [0, 0, 0], [0, 1, 0], [1, 1, 0]], shade: 0.8 },
];
const UV: [number, number][] = [[0, 0], [1, 0], [1, 1], [0, 1]];
const AO = [0.55, 0.7, 0.85, 1];

// Leaves and glass don't hide neighbours, so the trees look properly bushy.
const OCCLUDES = SOLID.map((s, b) => (s === 1 && b !== B.LEAVES && b !== B.GLASS ? 1 : 0));
function occludes(b: number) {
  return OCCLUDES[b] === 1;
}

interface Buf {
  pos: number[];
  uv: number[];
  col: number[];
  idx: number[];
}
const newBuf = (): Buf => ({ pos: [], uv: [], col: [], idx: [] });

function toGeometry(b: Buf): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(b.pos, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(b.uv, 2));
  g.setAttribute("color", new THREE.Float32BufferAttribute(b.col, 3));
  g.setIndex(b.idx);
  g.computeBoundingSphere();
  return g;
}

export function buildWorldMeshes(world: World, atlasTex: THREE.Texture): THREE.Group {
  const group = new THREE.Group();
  const solidMat = new THREE.MeshBasicMaterial({ map: atlasTex, vertexColors: true, alphaTest: 0.5 });
  const waterMat = new THREE.MeshBasicMaterial({ map: atlasTex, vertexColors: true, transparent: true, depthWrite: false, side: THREE.DoubleSide });
  const eps = 0.001;
  const CH = 16;
  const SX = world.sx, SY = world.sy, SZ = world.sz;

  // Most of a map is buried stone with nothing to draw. Per column, find the
  // first block that lets light through (air, water, leaves, glass) and the top
  // block; anything below every neighbour's first see-through block is hidden.
  const open = new Int16Array(SX * SZ);
  const top = new Int16Array(SX * SZ);
  for (let z = 0; z < SZ; z++) for (let x = 0; x < SX; x++) {
    let y = 0;
    while (y < SY && OCCLUDES[world.blocks[(y * SZ + z) * SX + x]!]) y++;
    open[z * SX + x] = y;
    let t = SY - 1;
    while (t >= 0 && world.blocks[(t * SZ + z) * SX + x] === B.AIR) t--;
    top[z * SX + x] = t;
  }
  const from = new Int16Array(SX * SZ);
  for (let z = 0; z < SZ; z++) for (let x = 0; x < SX; x++) {
    const i = z * SX + x;
    let lo = open[i]! - 1;
    if (x > 0) lo = Math.min(lo, open[i - 1]!);
    if (x < SX - 1) lo = Math.min(lo, open[i + 1]!);
    if (z > 0) lo = Math.min(lo, open[i - SX]!);
    if (z < SZ - 1) lo = Math.min(lo, open[i + SX]!);
    from[i] = Math.max(0, lo);
  }

  for (let cz = 0; cz < SZ; cz += CH) {
    for (let cx = 0; cx < SX; cx += CH) {
      const solid = newBuf();
      const water = newBuf();
      let y0 = SY, y1 = -1;
      for (let z = cz; z < Math.min(SZ, cz + CH); z++) for (let x = cx; x < Math.min(SX, cx + CH); x++) {
        y0 = Math.min(y0, from[z * SX + x]!);
        y1 = Math.max(y1, top[z * SX + x]!);
      }
      for (let y = y0; y <= y1; y++) for (let z = cz; z < cz + CH; z++) for (let x = cx; x < cx + CH; x++) {
        if (x >= SX || z >= SZ) continue;
        const col = z * SX + x;
        if (y < from[col]! || y > top[col]!) continue;
        const b = world.blocks[(y * SZ + z) * SX + x]!;
        if (b === B.AIR) continue;
        const isWater = b === B.WATER;
        for (let dir = 0; dir < 6; dir++) {
          const f = FACES[dir]!;
          const nb = world.get(x + f.n[0], y + f.n[1], z + f.n[2]);
          if (isWater) {
            if (nb === B.WATER || occludes(nb)) continue;
          } else {
            if (occludes(nb)) continue;
            if (nb === b && (b === B.GLASS)) continue;
            if (x + f.n[0] < 0 || z + f.n[2] < 0 || x + f.n[0] >= SX || z + f.n[2] >= SZ) continue;
          }
          const buf = isWater ? water : solid;
          const tile = blockTile(b, dir);
          const u0 = (tile % ATLAS_COLS) / ATLAS_COLS;
          const v0 = 1 - (Math.floor(tile / ATLAS_COLS) + 1) / ATLAS_ROWS;
          const du = 1 / ATLAS_COLS, dv = 1 / ATLAS_ROWS;
          const base = buf.pos.length / 3;
          const ao: number[] = [];
          for (let k = 0; k < 4; k++) {
            const c = f.c[k]!;
            let py = y + c[1];
            if (isWater && c[1] === 1 && world.get(x, y + 1, z) !== B.WATER) py -= 0.12;
            buf.pos.push(x + c[0], py, z + c[2]);
            buf.uv.push(u0 + (UV[k]![0] === 1 ? du - eps : eps), v0 + (UV[k]![1] === 1 ? dv - eps : eps));
            const a = isWater ? 3 : vertexAO(world, x, y, z, AO_OFFSETS[dir]![k]!);
            ao.push(a);
            // Minecraft applies face shading in sRGB; convert so it reads the same here.
            const l = Math.pow(f.shade * AO[a]!, 2.2);
            buf.col.push(l, l, l);
          }
          if (ao[0]! + ao[2]! < ao[1]! + ao[3]!) buf.idx.push(base + 1, base + 2, base + 3, base + 1, base + 3, base);
          else buf.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
        }
      }
      if (solid.idx.length) group.add(new THREE.Mesh(toGeometry(solid), solidMat));
      if (water.idx.length) {
        const m = new THREE.Mesh(toGeometry(water), waterMat);
        m.renderOrder = 2;
        group.add(m);
      }
    }
  }
  return group;
}

// For each face and corner, the three blocks AO samples: in the layer in front of
// the face, toward the corner along each tangent, and diagonally. Precomputed
// because this runs for every vertex of a 160x160 map.
const AO_OFFSETS: Int8Array[][] = FACES.map((f) =>
  f.c.map((c) => {
    const axes = [0, 1, 2].filter((a) => f.n[a] === 0);
    const o1 = [...f.n], o2 = [...f.n];
    o1[axes[0]!]! += c[axes[0]!] === 1 ? 1 : -1;
    o2[axes[1]!]! += c[axes[1]!] === 1 ? 1 : -1;
    const oc = [o1[0]! + o2[0]! - f.n[0], o1[1]! + o2[1]! - f.n[1], o1[2]! + o2[2]! - f.n[2]];
    return Int8Array.from([...o1, ...o2, ...oc]);
  }),
);

function vertexAO(w: World, x: number, y: number, z: number, o: Int8Array): number {
  const s1 = OCCLUDES[w.get(x + o[0]!, y + o[1]!, z + o[2]!)]!;
  const s2 = OCCLUDES[w.get(x + o[3]!, y + o[4]!, z + o[5]!)]!;
  if (s1 && s2) return 0;
  return 3 - (s1 + s2 + OCCLUDES[w.get(x + o[6]!, y + o[7]!, z + o[8]!)]!);
}
